// Sprint T-22 · NotificationPreferencesPanel · matriz kind × canal
//
// Consume /api/notifications/preferences (dual auth · user + customer).
// Cambio inmediato con toast · rollback optimista si falla.
//
// Regla especial: `security_alert` es READ-ONLY (backend rechaza 400).
// SMS/WhatsApp no aparecen · directriz Founder §7.

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "@phosphor-icons/react";
import { Switch } from "../ui/switch";
import {
  OFFICIAL_KINDS,
  KINDS_MAP,
  OFFICIAL_CHANNELS,
} from "../../lib/notificationKindsMap";

/**
 * Panel de preferencias granulares · matriz kind × canal.
 *
 * Props:
 *   · apiClient → axios instance ya autenticado (usa /api/notifications/preferences).
 */
export const NotificationPreferencesPanel = ({ apiClient }) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get("/notifications/preferences");
      setPrefs(r.data || {});
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError("No autenticado");
      else setError("No se pudieron cargar las preferencias");
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  // Devuelve el flag actual del kind/channel (default: true si el kind soporta ese canal).
  const isChannelEnabled = (kind, channel) => {
    const map = prefs?.kind_channels?.[kind];
    if (map && Object.prototype.hasOwnProperty.call(map, channel)) {
      return map[channel] !== false;
    }
    return true; // opt-in por defecto (el usuario NO ha desactivado explícitamente)
  };

  const toggleKindChannel = async (kind, channel, nextValue) => {
    const meta = KINDS_MAP[kind];
    if (!meta) return;

    // Si el canal no está soportado para ese kind, no permitimos toggle.
    if (!meta.channels.includes(channel)) return;

    // security_alert es locked (nunca se puede desactivar).
    if (meta.locked && nextValue === false) {
      toast.error("Las alertas de seguridad no se pueden desactivar");
      return;
    }

    // Optimistic update
    const prevPrefs = prefs;
    const newKindChannels = {
      ...(prefs?.kind_channels || {}),
      [kind]: {
        ...(prefs?.kind_channels?.[kind] || {}),
        [channel]: nextValue,
      },
    };
    setPrefs({ ...prefs, kind_channels: newKindChannels });
    setSaving(true);

    try {
      const r = await apiClient.put("/notifications/preferences", {
        kind_channels: { [kind]: { [channel]: nextValue } },
      });
      setPrefs(r.data || prefs);
      toast.success(
        nextValue
          ? `${meta.label} · ${channelLabel(channel)} activado`
          : `${meta.label} · ${channelLabel(channel)} desactivado`,
      );
    } catch (e) {
      // Rollback
      setPrefs(prevPrefs);
      const detail =
        e?.response?.data?.detail || "No se pudo actualizar la preferencia";
      toast.error(String(detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="p-6 text-sm text-[var(--ag-text-dim)]"
        data-testid="notif-prefs-panel-loading"
      >
        Cargando preferencias…
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="p-6 text-sm text-red-300"
        data-testid="notif-prefs-panel-error"
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className="space-y-4"
      data-testid="notification-preferences-panel"
      aria-busy={saving}
    >
      <div className="px-1">
        <h3 className="text-sm font-semibold text-white">
          Preferencias granulares por categoría
        </h3>
        <p className="text-xs text-[var(--ag-text-dim)] mt-1">
          Elige por qué canal quieres recibir cada tipo de notificación. Los
          canales oficiales son <strong>In-App</strong>, <strong>Push</strong> y{" "}
          <strong>Email</strong>.
        </p>
      </div>

      {/* Tabla accesible */}
      <div
        className="border border-[var(--ag-border)] rounded-md overflow-hidden"
        role="table"
        aria-label="Preferencias por categoría y canal"
      >
        {/* Header */}
        <div
          className="grid grid-cols-[minmax(0,1fr)_60px_60px_60px] gap-2 px-4 py-2 bg-[var(--ag-midnight)]/70 border-b border-[var(--ag-border)] text-[10px] uppercase tracking-widest font-bold text-[var(--ag-text-dim)]"
          role="row"
        >
          <div role="columnheader">Categoría</div>
          {OFFICIAL_CHANNELS.map((c) => (
            <div key={c.key} role="columnheader" className="text-center">
              {c.label}
            </div>
          ))}
        </div>

        {/* Filas */}
        {OFFICIAL_KINDS.map((kind) => {
          const meta = KINDS_MAP[kind];
          const Icon = meta.icon;
          return (
            <div
              key={kind}
              className="grid grid-cols-[minmax(0,1fr)_60px_60px_60px] gap-2 px-4 py-3 border-b border-[var(--ag-border)]/50 last:border-b-0 items-center hover:bg-[var(--ag-midnight)]/40"
              role="row"
              data-testid={`notif-prefs-row-${kind}`}
            >
              <div
                role="cell"
                className="flex items-center gap-2 min-w-0"
              >
                <div className="shrink-0 w-8 h-8 rounded-md bg-[var(--ag-midnight)]/70 border border-[var(--ag-border)]/60 flex items-center justify-center text-[var(--ag-amber,#f59e0b)]">
                  <Icon size={15} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate flex items-center gap-1.5">
                    {meta.label}
                    {meta.locked && (
                      <Lock
                        size={11}
                        className="text-[var(--ag-amber,#f59e0b)]"
                        aria-label="Bloqueado · no se puede desactivar"
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5 line-clamp-1">
                    {meta.description}
                  </div>
                </div>
              </div>

              {OFFICIAL_CHANNELS.map((c) => {
                const supported = meta.channels.includes(c.key);
                const enabled = supported ? isChannelEnabled(kind, c.key) : false;
                const disabled = !supported || meta.locked || saving;
                return (
                  <div
                    key={c.key}
                    role="cell"
                    className="flex justify-center items-center"
                  >
                    {supported ? (
                      <Switch
                        checked={enabled}
                        onCheckedChange={(v) => toggleKindChannel(kind, c.key, v)}
                        disabled={disabled}
                        aria-label={`${meta.label} · ${c.label}`}
                        title={
                          meta.locked
                            ? "Esta categoría no se puede desactivar"
                            : `${meta.label} · ${c.label}`
                        }
                        data-testid={`notif-prefs-switch-${kind}-${c.key}`}
                      />
                    ) : (
                      <span
                        className="text-[var(--ag-text-dim)]/50 text-xs"
                        aria-label="Canal no aplicable para esta categoría"
                        data-testid={`notif-prefs-na-${kind}-${c.key}`}
                      >
                        —
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer info */}
      <div className="flex items-center gap-2 text-[11px] text-[var(--ag-text-dim)] px-1">
        <ShieldCheck size={12} weight="fill" className="text-emerald-400" />
        <span>
          Las alertas de seguridad no se pueden desactivar por tu protección.
          SMS y WhatsApp no forman parte de los canales oficiales.
        </span>
      </div>
    </div>
  );
};

function channelLabel(key) {
  return OFFICIAL_CHANNELS.find((c) => c.key === key)?.label || key;
}

export default NotificationPreferencesPanel;
